import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, Plus, Trash2, Upload, ChevronRight, ChevronLeft, Check } from "lucide-react";
import { DriverShell } from "@/components/shells";
import { Panel, Spinner, Btn, Field, Input, Textarea } from "@/components/kit";
import { AddressLookup } from "@/components/AddressLookup";
import { SketchPad } from "@/components/SketchPad";
import { api, fileToPhotoPayload } from "@/lib/api";

const STEPS = ["Incident", "Your vehicle", "Other parties", "Evidence", "Review"];
const PARTY_KINDS = [["third_party", "Third party"], ["passenger", "Passenger"], ["witness", "Witness"]];

function YesNo({ testId, value, onChange, danger }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {[["yes", "Yes"], ["no", "No"]].map(([v, l]) => (
        <button key={v} type="button" data-testid={`${testId}-${v}`} onClick={() => onChange(v)}
          className={`h-11 rounded-[6px] border text-sm font-medium ${value === v ? (danger && v === "yes" ? "border-[hsl(var(--vda-red))] bg-[hsl(var(--vda-red))]/10 text-[hsl(var(--vda-red))]" : "border-primary bg-primary/10 text-primary") : "border-[hsl(var(--input))]"}`}>{l}</button>
      ))}
    </div>
  );
}

export default function DriverAccident() {
  const nav = useNavigate();
  const [aid, setAid] = useState(null);
  const [ref, setRef] = useState(null);
  const [step, setStep] = useState(0);
  const [creating, setCreating] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    incident_at: "", location_text: "", postcode: "", address: {}, how_occurred: "",
    police_attended: "no", police_reference: "", injuries_reported: "no",
    vehicle_driveable: "yes", recovery_required: "no", damage_areas: "", description: "",
  });
  const [parties, setParties] = useState([]);
  const [newParty, setNewParty] = useState({ party_kind: "third_party", name: "", vehicle_reg: "", insurer: "", contact_phone: "", notes: "" });
  const [evidence, setEvidence] = useState([]);
  const [sketchSaving, setSketchSaving] = useState(false);
  const [sketchSaved, setSketchSaved] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveSketch = async (dataUrl) => {
    setSketchSaving(true);
    try {
      await api.accidentAddEvidence(aid, [{ data_base64: dataUrl, content_type: "image/png" }], ["Scene sketch"], "sketch");
      setSketchSaved(true);
      toast.success("Scene sketch saved.");
    } catch (e) { toast.error(e.message || "Could not save the sketch."); }
    finally { setSketchSaving(false); }
  };

  useEffect(() => {
    api.accidentCreate({}).then((r) => { setAid(r.id); setRef(r.human_reference); }).catch((e) => toast.error(e.message)).finally(() => setCreating(false));
  }, []);

  const persistStep = async () => {
    if (!aid) return;
    setSaving(true);
    try {
      await api.accidentUpdate(aid, {
        incident_at: form.incident_at ? new Date(form.incident_at).toISOString() : null,
        location_text: form.address?.address_text || form.location_text || null,
        postcode: form.address?.postcode || form.postcode || null,
        how_occurred: form.how_occurred || null, description: form.description || null,
        police_attended: form.police_attended === "yes", police_reference: form.police_reference || null,
        injuries_reported: form.injuries_reported === "yes",
        vehicle_driveable: form.vehicle_driveable === "yes",
        recovery_required: form.recovery_required === "yes",
        our_vehicle: { damage_areas: form.damage_areas || null },
        current_step: step + 1,
      });
    } catch (e) { toast.error(e.message || "Could not save progress."); } finally { setSaving(false); }
  };

  const next = async () => { await persistStep(); setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const addParty = async () => {
    try {
      const r = await api.accidentAddParty(aid, newParty);
      setParties((p) => [...p, { id: r.id, ...newParty }]);
      setNewParty({ party_kind: newParty.party_kind, name: "", vehicle_reg: "", insurer: "", contact_phone: "", notes: "" });
      toast.success("Added.");
    } catch (e) { toast.error(e.message); }
  };
  const removeParty = async (pid) => { await api.accidentDeleteParty(aid, pid); setParties((p) => p.filter((x) => x.id !== pid)); };

  const addEvidence = async (files) => {
    if (!files?.length) return;
    try {
      const payloads = await Promise.all(Array.from(files).slice(0, 10).map(fileToPhotoPayload));
      const res = await api.accidentAddEvidence(aid, payloads.map((p) => ({ data_base64: p.data_base64, content_type: p.content_type })), payloads.map((p) => p.name));
      setEvidence((e) => [...e, ...payloads.slice(0, res.uploaded).map((p) => p.name)]);
      toast.success(`${res.uploaded} photo(s) added.`);
    } catch (e) { toast.error(e.message || "Upload failed."); }
  };

  const submit = async () => {
    await persistStep();
    setSaving(true);
    try {
      const res = await api.accidentSubmit(aid);
      toast.success("Accident report submitted to your Venson team.");
      nav(`/driver/booking/${res.booking_request_id}`, { replace: true });
    } catch (e) { toast.error(e.message || "Could not submit."); } finally { setSaving(false); }
  };

  if (creating) return <DriverShell back="/driver"><Spinner /></DriverShell>;

  return (
    <DriverShell back="/driver" title="Accident & bodywork">
      <div data-testid="driver-accident">
        <Panel className="p-4 mb-4 bg-[hsl(var(--vda-red))]/8 border-[hsl(var(--vda-red))]/30 flex gap-2" data-testid="accident-notice">
          <ShieldAlert className="h-5 w-5 text-[hsl(var(--vda-red))] flex-none" />
          <p className="text-sm text-foreground/80">If anyone is hurt or in danger, call <strong>999</strong> first. This logs the incident — it does not summon emergency services.</p>
        </Panel>

        <div className="flex items-center gap-1.5 mb-4" data-testid="accident-steps">
          {STEPS.map((s, i) => (
            <div key={s} className={`flex-1 h-1.5 rounded-full ${i <= step ? "bg-primary" : "bg-secondary"}`} title={s} />
          ))}
        </div>
        <p className="vda-tick text-[hsl(var(--vda-quiet))] mb-1">Step {step + 1} of {STEPS.length} · {ref}</p>
        <h2 className="text-lg font-semibold mb-3">{STEPS[step]}</h2>

        {step === 0 && (
          <div className="space-y-4">
            <Field label="When did it happen?"><Input data-testid="acc-datetime" type="datetime-local" value={form.incident_at} onChange={(e) => set("incident_at", e.target.value)} /></Field>
            <AddressLookup label="Where did it happen?" value={form.address} onChange={(a) => set("address", a)} testIdPrefix="acc-address" />
            <Field label="How did it occur?"><Textarea data-testid="acc-how" rows={4} value={form.how_occurred} onChange={(e) => set("how_occurred", e.target.value)} placeholder="Describe what happened" /></Field>
            <Field label="Did the police attend?"><YesNo testId="acc-police" value={form.police_attended} onChange={(v) => set("police_attended", v)} /></Field>
            {form.police_attended === "yes" && <Field label="Police reference"><Input data-testid="acc-police-ref" value={form.police_reference} onChange={(e) => set("police_reference", e.target.value)} /></Field>}
            <Field label="Was anyone injured?"><YesNo testId="acc-injuries" value={form.injuries_reported} onChange={(v) => set("injuries_reported", v)} danger /></Field>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <Field label="Is your vehicle driveable?"><YesNo testId="acc-driveable" value={form.vehicle_driveable} onChange={(v) => set("vehicle_driveable", v)} /></Field>
            <Field label="Does it need recovery?"><YesNo testId="acc-recovery" value={form.recovery_required} onChange={(v) => set("recovery_required", v)} danger /></Field>
            <Field label="Which areas are damaged?"><Textarea data-testid="acc-damage" rows={3} value={form.damage_areas} onChange={(e) => set("damage_areas", e.target.value)} placeholder="e.g. Front nearside wing, bumper" /></Field>
            <Field label="Anything else about the damage?"><Textarea data-testid="acc-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <Panel className="p-4" data-testid="acc-party-form">
              <div className="flex flex-wrap gap-1.5 mb-3">
                {PARTY_KINDS.map(([v, l]) => (
                  <button key={v} data-testid={`acc-party-kind-${v}`} onClick={() => setNewParty({ ...newParty, party_kind: v })}
                    className={`h-9 px-3 rounded-[6px] border text-xs font-medium ${newParty.party_kind === v ? "border-primary text-primary bg-primary/10" : "border-[hsl(var(--input))] text-muted-foreground"}`}>{l}</button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="acc-party-name" value={newParty.name} onChange={(e) => setNewParty({ ...newParty, name: e.target.value })} placeholder="Name" />
                <Input data-testid="acc-party-phone" value={newParty.contact_phone} onChange={(e) => setNewParty({ ...newParty, contact_phone: e.target.value })} placeholder="Phone" />
                {newParty.party_kind === "third_party" && <>
                  <Input data-testid="acc-party-reg" value={newParty.vehicle_reg} onChange={(e) => setNewParty({ ...newParty, vehicle_reg: e.target.value })} placeholder="Vehicle reg" />
                  <Input data-testid="acc-party-insurer" value={newParty.insurer} onChange={(e) => setNewParty({ ...newParty, insurer: e.target.value })} placeholder="Insurer" />
                </>}
              </div>
              <Btn data-testid="acc-party-add" variant="outline" className="mt-2" onClick={addParty} disabled={!newParty.name}><Plus className="h-4 w-4" /> Add {PARTY_KINDS.find((k) => k[0] === newParty.party_kind)[1].toLowerCase()}</Btn>
            </Panel>
            {parties.length === 0 ? <p className="text-sm text-muted-foreground">No other parties added yet. Add any third parties, passengers or witnesses.</p> : (
              <ul className="space-y-2" data-testid="acc-party-list">
                {parties.map((p) => (
                  <li key={p.id} className="vda-panel p-3 flex items-center justify-between" data-testid={`acc-party-${p.id}`}>
                    <span className="text-sm"><strong>{p.name || "Unnamed"}</strong> <span className="text-xs text-muted-foreground">· {PARTY_KINDS.find((k) => k[0] === p.party_kind)?.[1]}{p.vehicle_reg ? ` · ${p.vehicle_reg}` : ""}</span></span>
                    <button data-testid={`acc-party-remove-${p.id}`} onClick={() => removeParty(p.id)} className="text-muted-foreground hover:text-[hsl(var(--vda-red))]"><Trash2 className="h-4 w-4" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold mb-1">Scene sketch <span className="text-xs text-muted-foreground font-normal">(optional)</span></p>
              <p className="text-xs text-muted-foreground mb-2">Draw a quick top-down sketch — road layout, vehicle positions and direction of travel.</p>
              <SketchPad onSave={saveSketch} saving={sketchSaving} saved={sketchSaved} />
            </div>
            <div>
              <p className="text-sm font-semibold mb-1">Photos</p>
              <label className="vda-panel p-4 flex items-center gap-2 cursor-pointer text-sm text-muted-foreground">
                <Upload className="h-4 w-4" /> {evidence.length ? "Add more photos" : "Add photos of the scene & damage"}
                <input data-testid="acc-evidence" type="file" accept="image/*" multiple className="hidden" onChange={(e) => addEvidence(e.target.files)} />
              </label>
              {evidence.length > 0 && <ul className="space-y-1 mt-2" data-testid="acc-evidence-list">{evidence.map((n, i) => <li key={i} className="vda-panel p-2.5 text-sm flex items-center gap-2"><Check className="h-4 w-4 text-primary" />{n}</li>)}</ul>}
            </div>
          </div>
        )}

        {step === 4 && (
          <Panel className="p-4 space-y-2 text-sm" data-testid="acc-review">
            <div><strong>Reference:</strong> {ref}</div>
            <div><strong>When:</strong> {form.incident_at || "—"}</div>
            <div><strong>Where:</strong> {form.address?.address_text || form.location_text || "—"}</div>
            <div><strong>How:</strong> {form.how_occurred || "—"}</div>
            <div><strong>Injuries:</strong> {form.injuries_reported === "yes" ? "Yes" : "No"} · <strong>Police:</strong> {form.police_attended === "yes" ? (form.police_reference || "Yes") : "No"}</div>
            <div><strong>Driveable:</strong> {form.vehicle_driveable === "yes" ? "Yes" : "No"} · <strong>Recovery:</strong> {form.recovery_required === "yes" ? "Yes" : "No"}</div>
            <div><strong>Damage:</strong> {form.damage_areas || "—"}</div>
            <div><strong>Other parties:</strong> {parties.length} · <strong>Photos:</strong> {evidence.length} · <strong>Sketch:</strong> {sketchSaved ? "Yes" : "No"}</div>
          </Panel>
        )}

        <div className="flex items-center justify-between gap-3 mt-6">
          <Btn variant="outline" onClick={back} disabled={step === 0 || saving} data-testid="acc-back"><ChevronLeft className="h-4 w-4" /> Back</Btn>
          {step < STEPS.length - 1 ? (
            <Btn onClick={next} disabled={saving} data-testid="acc-next">{saving ? "Saving…" : "Next"} <ChevronRight className="h-4 w-4" /></Btn>
          ) : (
            <Btn onClick={submit} disabled={saving} data-testid="acc-submit">{saving ? "Submitting…" : "Submit report"}</Btn>
          )}
        </div>
      </div>
    </DriverShell>
  );
}
